// ===================================================================
// DEBRIS MANAGEMENT — Tampa Module Layout
// Contractor data ingestion, reconciliation, cost tracking, FEMA packages
// ===================================================================

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useDebrisStore } from '../../store/debris-store';
import {
  IconLayoutDashboard, IconMap2, IconBuildingFactory2,
  IconTicket, IconBuildingWarehouse,
  IconFileInvoice, IconChartBar, IconTrendingUp,
  IconScale, IconPackage, IconFileText,
  IconTruck, IconArchive,
  IconUsers, IconClock,
  IconShieldCheck, IconClipboardList, IconChecklist,
  IconInfoCircle, IconListDetails,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import ModuleNav from '../../components/layout/ModuleNav';
import type { NavItem } from '../../types/nav';
import PageHeader from '../../components/ui/PageHeader';

const NAV_ITEMS: NavItem[] = [
  { label: 'About / How To',  path: '/debris',                icon: IconInfoCircle, exact: true },
  { label: 'Dashboard',       path: '/debris/dashboard',      icon: IconLayoutDashboard },
  { label: 'Data Intake',      path: '/debris/processor',     icon: IconPackage },
  { label: 'Load Tickets',    path: '/debris/tickets',        icon: IconTicket },
  // { label: 'Zones & Routes',  path: '/debris/zones',          icon: IconMap2 },
  { label: 'Disposal Sites',  path: '/debris/sites',          icon: IconBuildingWarehouse },
  { label: 'Truck Registry',  path: '/debris/trucks',         icon: IconTruck },
  { label: 'Contractors',     path: '/debris/contractors',    icon: IconBuildingFactory2 },
  { label: 'Reconciliation',  path: '/debris/reconciliation', icon: IconScale, minLevel: 'editor' },
  { label: 'Invoices',        path: '/debris/invoices',       icon: IconFileInvoice },
  { label: 'Cost Dashboard',  path: '/debris/cost',           icon: IconChartBar },
  { label: 'FEMA Packages',   path: '/debris/fema',           icon: IconPackage, minLevel: 'editor' },
  { label: 'PW Register',    path: '/debris/pw',             icon: IconClipboardList, minLevel: 'editor' },
  { label: 'FEMA PA Forms',  path: '/debris/femaforms',      icon: IconFileText, minLevel: 'editor' },
  { label: 'Documentation',    path: '/debris/evidence',       icon: IconArchive },
  { label: 'Pre-Sub Audit',   path: '/debris/audit',          icon: IconShieldCheck, minLevel: 'editor' },
  // { label: 'Activity Log',    path: '/debris/activity',       icon: IconHistory, minLevel: 'editor' },
  { label: 'Rate Schedules',  path: '/debris/rates',          icon: IconListDetails, minLevel: 'editor' },
  { label: 'Doc Requirements', path: '/debris/document-requirements', icon: IconChecklist, minLevel: 'editor' },
  { label: 'Analytics',       path: '/debris/analytics',      icon: IconTrendingUp },
  { label: 'Map',             path: '/debris/map',            icon: IconMap2 },
  { label: 'Reports',         path: '/debris/reports',        icon: IconFileText },
  { label: 'Personnel Roster', path: '/debris/personnel',     icon: IconUsers },
  { label: 'Force Account',   path: '/debris/forceaccount',   icon: IconClock, minLevel: 'editor' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/debris', '/debris/processor'] },
  { label: 'Load Tickets', paths: ['/debris/tickets', '/debris/zones', '/debris/sites', '/debris/trucks'] },
  { label: 'Financial', paths: ['/debris/contractors', '/debris/reconciliation', '/debris/invoices', '/debris/cost', '/debris/document-requirements'] },
  { label: 'FEMA', paths: ['/debris/fema', '/debris/pw', '/debris/femaforms', '/debris/evidence', '/debris/audit', '/debris/activity'] },
  { label: 'Reports', paths: ['/debris/rates', '/debris/analytics', '/debris/map', '/debris/reports'] },
  { label: 'Workforce', paths: ['/debris/personnel', '/debris/forceaccount'] },
];

export default function DebrisLayout() {
  const visibleItems = useFilteredNav('debris', NAV_ITEMS);
  const activeIncidentId = useAuthStore(s => s.activeIncident?.id);

  useEffect(() => {
    if (!activeIncidentId) return;
    // Re-fetch all incident-scoped debris data when active incident changes
    useDebrisOpsStore.getState().loadLoadTickets();
    useDebrisOpsStore.getState().loadInvoices();
    useTampaDebrisStore.getState().loadReconciliations();
    useTampaDebrisStore.getState().loadFemaPackages();
    useTampaDebrisStore.getState().loadImports();
    useDebrisStore.getState().fetchContractors();
    useDebrisStore.getState().fetchDisposalSites();
    useDebrisStore.getState().fetchZones();
  }, [activeIncidentId]);

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Recover' }, { label: 'Debris Management' }]}
        title="Debris Management"
        subtitle="Contractor data ingestion, invoice reconciliation, and FEMA reimbursement tracking"
      />
      <ModuleNav items={visibleItems} groups={groups} />
      <Outlet />
    </div>
  );
}
